/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import {
  buildPrenigmaStreamingVoiceTurnEvidence,
  type BuildPrenigmaStreamingVoiceTurnEvidenceOptions,
  type PrenigmaStreamingVoiceTurnEvidence,
} from "./streamingVoiceTurnEvidence.ts";

const logger = rootLogger.getChild("[PrenigmaStreamingVoiceEvidenceBridge]");

type ContextualEvidenceField =
  | "productId"
  | "roomId"
  | "sessionId"
  | "tenantId"
  | "workspaceId"
  | "workflowId";

export type PrenigmaStreamingVoiceEvidenceBridgeContext = Partial<
  Pick<BuildPrenigmaStreamingVoiceTurnEvidenceOptions, ContextualEvidenceField>
>;

export interface PrenigmaStreamingVoiceEvidenceRequest extends Omit<
  BuildPrenigmaStreamingVoiceTurnEvidenceOptions,
  ContextualEvidenceField
> {
  productId?: string;
  requestId: string;
  roomId?: string;
  sessionId?: string;
  tenantId?: string;
  workspaceId?: string;
  workflowId?: string;
}

export type PrenigmaStreamingVoiceEvidenceAcknowledgementStatus =
  | "built"
  | "duplicate_ignored"
  | "invalid_request";

export interface PrenigmaStreamingVoiceEvidenceAcknowledgement {
  evidence?: PrenigmaStreamingVoiceTurnEvidence;
  error?: string;
  providerCredentialsReturned: false;
  rawAudioReturned: false;
  rawSecretMaterialReturned: false;
  rawTranscriptReturned: false;
  requestId: string;
  status: PrenigmaStreamingVoiceEvidenceAcknowledgementStatus;
}

export interface PrenigmaStreamingVoiceEvidenceRuntimeHost {
  acknowledgeStreamingVoiceTurnEvidenceRequest?: (
    acknowledgement: PrenigmaStreamingVoiceEvidenceAcknowledgement,
  ) => void;
  onStreamingVoiceTurnEvidenceRequest?: (
    handler: (request: PrenigmaStreamingVoiceEvidenceRequest) => void,
  ) => (() => void) | void;
  readPendingStreamingVoiceTurnEvidenceRequests?: () => PrenigmaStreamingVoiceEvidenceRequest[];
  requestStreamingVoiceTurnEvidence?: (
    request: PrenigmaStreamingVoiceEvidenceRequest,
  ) => void;
}

export type PrenigmaStreamingVoiceEvidenceBackendPostStatus =
  | "invalid_evidence"
  | "not_posted"
  | "post_failed"
  | "posted";

export interface PrenigmaStreamingVoiceEvidenceBackendPostResult {
  error?: string;
  providerCredentialsReturned: false;
  rawAudioReturned: false;
  rawSecretMaterialReturned: false;
  rawTranscriptReturned: false;
  recorded?: boolean;
  requestId: string;
  responseStatus?: number;
  status: PrenigmaStreamingVoiceEvidenceBackendPostStatus;
  streamingEvidenceRecorded?: boolean;
}

export interface ConnectPrenigmaStreamingVoiceEvidenceBridgeOptions {
  context?: PrenigmaStreamingVoiceEvidenceBridgeContext;
  host?: PrenigmaStreamingVoiceEvidenceRuntimeHost;
}

export interface CreatePrenigmaStreamingVoiceEvidenceApiHostOptions {
  apiPath?: string;
  fetchFn?: typeof fetch;
  onBackendPostResult?: (
    result: PrenigmaStreamingVoiceEvidenceBackendPostResult,
  ) => void;
}

export interface PrenigmaStreamingVoiceEvidenceBridge {
  disconnect: () => void;
}

export function connectPrenigmaStreamingVoiceEvidenceBridge({
  context = {},
  host,
}: ConnectPrenigmaStreamingVoiceEvidenceBridgeOptions): PrenigmaStreamingVoiceEvidenceBridge {
  if (host === undefined) {
    return { disconnect: (): void => {} };
  }

  let connected = true;
  const handledRequestIds = new Set<string>();
  const handleRequest = (
    request: PrenigmaStreamingVoiceEvidenceRequest,
  ): void => {
    if (!connected) return;
    handlePrenigmaStreamingVoiceEvidenceRequest({
      connected: () => connected,
      context,
      handledRequestIds,
      host,
      request,
    });
  };

  for (const request of readPendingStreamingVoiceEvidenceRequests(host)) {
    handleRequest(request);
  }

  const unsubscribe = host.onStreamingVoiceTurnEvidenceRequest?.(handleRequest);

  return {
    disconnect: (): void => {
      connected = false;
      unsubscribe?.();
    },
  };
}

export function createPrenigmaStreamingVoiceEvidenceApiHost({
  apiPath = "",
  fetchFn = globalThis.fetch?.bind(globalThis),
  onBackendPostResult,
}: CreatePrenigmaStreamingVoiceEvidenceApiHostOptions = {}):
  | PrenigmaStreamingVoiceEvidenceRuntimeHost
  | undefined {
  const cleanApiPath = apiPath.trim();
  if (!cleanApiPath || typeof fetchFn !== "function") return undefined;

  const pendingRequests: PrenigmaStreamingVoiceEvidenceRequest[] = [];
  const handlers = new Set<
    (request: PrenigmaStreamingVoiceEvidenceRequest) => void
  >();

  return {
    acknowledgeStreamingVoiceTurnEvidenceRequest: (acknowledgement): void => {
      void postStreamingVoiceEvidence({
        acknowledgement,
        apiPath: cleanApiPath,
        fetchFn,
        onBackendPostResult,
      });
    },
    onStreamingVoiceTurnEvidenceRequest: (handler): (() => void) => {
      handlers.add(handler);
      return (): void => {
        handlers.delete(handler);
      };
    },
    readPendingStreamingVoiceTurnEvidenceRequests: () =>
      pendingRequests.splice(0),
    requestStreamingVoiceTurnEvidence: (request): void => {
      if (handlers.size === 0) {
        pendingRequests.push(request);
        return;
      }
      for (const handler of [...handlers]) handler(request);
    },
  };
}

function handlePrenigmaStreamingVoiceEvidenceRequest({
  connected,
  context,
  handledRequestIds,
  host,
  request,
}: {
  connected: () => boolean;
  context: PrenigmaStreamingVoiceEvidenceBridgeContext;
  handledRequestIds: Set<string>;
  host: PrenigmaStreamingVoiceEvidenceRuntimeHost;
  request: PrenigmaStreamingVoiceEvidenceRequest;
}): void {
  const requestId = safeText(request?.requestId);
  if (requestId === undefined) {
    acknowledgeStreamingVoiceEvidenceRequest(host, {
      ...baseAcknowledgement("unknown"),
      error: "request_id_required",
      status: "invalid_request",
    });
    return;
  }

  if (handledRequestIds.has(requestId)) {
    acknowledgeStreamingVoiceEvidenceRequest(host, {
      ...baseAcknowledgement(requestId),
      error: "duplicate_request_id",
      status: "duplicate_ignored",
    });
    return;
  }
  handledRequestIds.add(requestId);

  try {
    const evidence = buildPrenigmaStreamingVoiceTurnEvidence(
      evidenceOptionsFromRequest(request, context),
    );
    if (!connected()) return;
    acknowledgeStreamingVoiceEvidenceRequest(host, {
      ...baseAcknowledgement(requestId),
      evidence,
      status: "built",
    });
  } catch (error) {
    if (!connected()) return;
    acknowledgeStreamingVoiceEvidenceRequest(host, {
      ...baseAcknowledgement(requestId),
      error: safeDiagnostic(error),
      status: "invalid_request",
    });
  }
}

function readPendingStreamingVoiceEvidenceRequests(
  host: PrenigmaStreamingVoiceEvidenceRuntimeHost,
): PrenigmaStreamingVoiceEvidenceRequest[] {
  try {
    return host.readPendingStreamingVoiceTurnEvidenceRequests?.() ?? [];
  } catch (error) {
    logger.warn("Prenigma streaming voice pending-request read failed", {
      error,
    });
    return [];
  }
}

function acknowledgeStreamingVoiceEvidenceRequest(
  host: PrenigmaStreamingVoiceEvidenceRuntimeHost,
  acknowledgement: PrenigmaStreamingVoiceEvidenceAcknowledgement,
): void {
  try {
    host.acknowledgeStreamingVoiceTurnEvidenceRequest?.(acknowledgement);
  } catch (error) {
    logger.warn("Prenigma streaming voice acknowledgement failed", {
      error,
      requestId: acknowledgement.requestId,
      status: acknowledgement.status,
    });
  }
}

function evidenceOptionsFromRequest(
  request: PrenigmaStreamingVoiceEvidenceRequest,
  context: PrenigmaStreamingVoiceEvidenceBridgeContext,
): BuildPrenigmaStreamingVoiceTurnEvidenceOptions {
  const { requestId: _requestId, ...requestOptions } = request;
  return {
    ...requestOptions,
    productId: firstText(request.productId, context.productId),
    roomId: firstText(request.roomId, context.roomId),
    sessionId: firstText(request.sessionId, context.sessionId),
    tenantId: firstText(request.tenantId, context.tenantId),
    workspaceId: firstText(request.workspaceId, context.workspaceId),
    workflowId: firstText(request.workflowId, context.workflowId),
  };
}

function baseAcknowledgement(
  requestId: string,
): Omit<PrenigmaStreamingVoiceEvidenceAcknowledgement, "status"> {
  return {
    providerCredentialsReturned: false,
    rawAudioReturned: false,
    rawSecretMaterialReturned: false,
    rawTranscriptReturned: false,
    requestId,
  };
}

function firstText(...values: Array<string | undefined>): string {
  return (
    values.find((value) => value !== undefined && value.trim() !== "") ?? ""
  );
}

function safeText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function safeDiagnostic(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/raw streaming voice material/i.test(message)) {
    return "streaming_voice_evidence_raw_material_rejected";
  }
  if (
    /api[_-]?key|audio_bytes|bearer|credential|password|raw_audio|secret|token|transcript_text/i.test(
      message,
    )
  ) {
    return "streaming_voice_evidence_invalid_request";
  }
  return message;
}

async function postStreamingVoiceEvidence({
  acknowledgement,
  apiPath,
  fetchFn,
  onBackendPostResult,
}: {
  acknowledgement: PrenigmaStreamingVoiceEvidenceAcknowledgement;
  apiPath: string;
  fetchFn: typeof fetch;
  onBackendPostResult?: (
    result: PrenigmaStreamingVoiceEvidenceBackendPostResult,
  ) => void;
}): Promise<void> {
  if (
    acknowledgement.status !== "built" ||
    acknowledgement.evidence === undefined
  ) {
    onBackendPostResult?.({
      ...baseBackendPostResult(acknowledgement.requestId),
      error: acknowledgement.error ?? "streaming_voice_evidence_not_built",
      status: "not_posted",
    });
    return;
  }

  const evidence = acknowledgement.evidence;
  if (!streamingEvidenceSafe(evidence)) {
    onBackendPostResult?.({
      ...baseBackendPostResult(acknowledgement.requestId),
      error: "streaming_voice_evidence_raw_material_rejected",
      status: "invalid_evidence",
    });
    return;
  }

  const trace = record(evidence.streaming_voice_turn_trace);
  const tenantId = text(trace.tenant_id);
  const workspaceId = text(trace.workspace_id);
  const roomId = text(trace.room_id);
  const url = streamingEvidenceIngestUrl(apiPath, tenantId);
  try {
    const response = await fetchFn(url, {
      body: JSON.stringify({
        workspace_id: workspaceId,
        room_id: roomId,
        element_call_live_evidence: {
          status: "ready",
          evidence_mode: "element_call_streaming_voice_evidence_api_host",
          room_id: roomId,
          streaming_voice_turn_evidence: evidence,
          raw_event_content_returned: false,
          raw_secret_material_returned: false,
        },
      }),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    });
    const parsed = await response
      .clone()
      .json()
      .catch(() => undefined);
    const ingest = record(record(parsed).element_call_live_evidence_ingest);
    const streamingEvidence = record(ingest.streaming_voice_evidence);
    onBackendPostResult?.({
      ...baseBackendPostResult(acknowledgement.requestId),
      recorded: ingest.recorded === true,
      responseStatus: response.status,
      status: response.ok ? "posted" : "post_failed",
      streamingEvidenceRecorded: streamingEvidence.recorded === true,
    });
    if (!response.ok) {
      logger.warn("Prenigma streaming voice evidence API delivery failed", {
        requestId: acknowledgement.requestId,
        responseStatus: response.status,
      });
    }
  } catch (error) {
    logger.warn("Prenigma streaming voice evidence API delivery errored", {
      error,
      requestId: acknowledgement.requestId,
    });
    onBackendPostResult?.({
      ...baseBackendPostResult(acknowledgement.requestId),
      error: "streaming_voice_evidence_api_delivery_failed",
      status: "post_failed",
    });
  }
}

function baseBackendPostResult(
  requestId: string,
): Omit<PrenigmaStreamingVoiceEvidenceBackendPostResult, "status"> {
  return {
    providerCredentialsReturned: false,
    rawAudioReturned: false,
    rawSecretMaterialReturned: false,
    rawTranscriptReturned: false,
    requestId,
  };
}

function streamingEvidenceIngestUrl(apiPath: string, tenantId: string): string {
  const cleanTenantId = tenantId.trim();
  if (!cleanTenantId) return apiPath;

  try {
    if (typeof window !== "undefined") {
      const url = new URL(apiPath, window.location.origin);
      if (!url.searchParams.has("tenant_id")) {
        url.searchParams.set("tenant_id", cleanTenantId);
      }
      if (/^https?:\/\//i.test(apiPath)) return url.href;
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return appendTenantQuery(apiPath, cleanTenantId);
  }

  return appendTenantQuery(apiPath, cleanTenantId);
}

function appendTenantQuery(apiPath: string, tenantId: string): string {
  const separator = apiPath.includes("?") ? "&" : "?";
  return `${apiPath}${separator}tenant_id=${encodeURIComponent(tenantId)}`;
}

function streamingEvidenceSafe(
  evidence: PrenigmaStreamingVoiceTurnEvidence,
): boolean {
  return Boolean(
    evidence.authenticated === true &&
    evidence.provider_credentials_returned === false &&
    evidence.raw_audio_returned === false &&
    evidence.raw_credentials_returned === false &&
    evidence.raw_secret_material_returned === false &&
    evidence.raw_transcript_returned === false &&
    !containsForbiddenEvidenceMaterial(evidence),
  );
}

const FALSE_ONLY_EVIDENCE_FLAGS = new Set([
  "provider_credentials_returned",
  "raw_audio_returned",
  "raw_credentials_returned",
  "raw_event_content_returned",
  "raw_secret_material_returned",
  "raw_transcript_returned",
]);

const RAW_MATERIAL_KEYS = new Set([
  "access_token",
  "api_key",
  "audio",
  "audio_base64",
  "audio_bytes",
  "audio_url",
  "authorization",
  "body",
  "content",
  "credentials",
  "id_token",
  "matrix_event_content",
  "message",
  "password",
  "provider_secret",
  "raw_audio",
  "raw_event_content",
  "raw_secret_material",
  "raw_text",
  "raw_transcript",
  "refresh_token",
  "secret",
  "source_event_content",
  "text",
  "token",
  "transcript",
  "transcript_text",
]);

function containsForbiddenEvidenceMaterial(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => containsForbiddenEvidenceMaterial(item));
  }
  if (value === null || typeof value !== "object") return false;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    const normalized = key.trim().toLowerCase();
    if (FALSE_ONLY_EVIDENCE_FLAGS.has(normalized) && item !== false) {
      return true;
    }
    if (
      RAW_MATERIAL_KEYS.has(normalized) &&
      item !== false &&
      item !== null &&
      item !== ""
    ) {
      return true;
    }
    if (containsForbiddenEvidenceMaterial(item)) return true;
  }
  return false;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
